'use client';

import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, RefreshCw, Smartphone, Globe, Flag, Hash } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { randomPhoneGeneratorContent } from '../../data/random-phone-generator-content';
import Link from 'next/link';

// Country codes and formats
const COUNTRY_FORMATS = [
    {
        name: 'United States',
        code: '+1',
        format: '###-###-####',
        flag: '🇺🇸',
    },
    {
        name: 'United Kingdom',
        code: '+44',
        format: '#### ######',
        flag: '🇬🇧',
    },
    {
        name: 'Canada',
        code: '+1',
        format: '###-###-####',
        flag: '🇨🇦',
    },
    {
        name: 'Australia',
        code: '+61',
        format: '#### #####',
        flag: '🇦🇺',
    },
    {
        name: 'Germany',
        code: '+49',
        format: '#### ########',
        flag: '🇩🇪',
    },
    {
        name: 'France',
        code: '+33',
        format: '# ## ## ## ##',
        flag: '🇫🇷',
    },
    {
        name: 'Japan',
        code: '+81',
        format: '###-####-####',
        flag: '🇯🇵',
    },
    {
        name: 'China',
        code: '+86',
        format: '### #### ####',
        flag: '🇨🇳',
    },
    {
        name: 'India',
        code: '+91',
        format: '##### #####',
        flag: '🇮🇳',
    },
    {
        name: 'Brazil',
        code: '+55',
        format: '## #####-####',
        flag: '🇧🇷',
    },
    {
        name: 'Russia',
        code: '+7',
        format: '### ###-##-##',
        flag: '🇷🇺',
    },
    {
        name: 'South Africa',
        code: '+27',
        format: '## ### ####',
        flag: '🇿🇦',
    },
    {
        name: 'Mexico',
        code: '+52',
        format: '### ### ####',
        flag: '🇲🇽',
    },
    {
        name: 'South Korea',
        code: '+82',
        format: '##-####-####',
        flag: '🇰🇷',
    },
    {
        name: 'Italy',
        code: '+39',
        format: '### #######',
        flag: '🇮🇹',
    },
    {
        name: 'Spain',
        code: '+34',
        format: '### ### ###',
        flag: '🇪🇸',
    },
    {
        name: 'Netherlands',
        code: '+31',
        format: '## #######',
        flag: '🇳🇱',
    },
    {
        name: 'Sweden',
        code: '+46',
        format: '###-### ## ##',
        flag: '🇸🇪',
    },
    {
        name: 'Switzerland',
        code: '+41',
        format: '## ### ## ##',
        flag: '🇨🇭',
    },
    {
        name: 'Singapore',
        code: '+65',
        format: '#### ####',
        flag: '🇸🇬',
    },
    {
        name: 'United Arab Emirates',
        code: '+971',
        format: '## ### ####',
        flag: '🇦🇪',
    },
    {
        name: 'Saudi Arabia',
        code: '+966',
        format: '## ### ####',
        flag: '🇸🇦',
    },
];

function generateRandomNumber(format: string): string {
    return format.replace(/#/g, () => Math.floor(Math.random() * 10).toString());
}

export default function RandomPhoneGenerator() {
    const [country, setCountry] = useState(COUNTRY_FORMATS[0]);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [copied, setCopied] = useState(false);
    const { toast } = useToast();

    const generatePhoneNumber = useCallback(() => {
        const number = generateRandomNumber(country.format);
        setPhoneNumber(number);
    }, [country.format]);

    const copyToClipboard = () => {
        const fullNumber = `${country.code} ${phoneNumber}`;
        navigator.clipboard.writeText(fullNumber).then(() => {
            setCopied(true);
            toast({
                title: 'Copied!',
                description: 'Phone number copied to clipboard',
                variant: 'success',
            });
            setTimeout(() => setCopied(false), 2000);
        });
    };

    // Generate initial number on mount
    useState(() => {
        generatePhoneNumber();
    });

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="max-w-3xl mx-auto">
                <ToolPageIntro title="Random Phone Generator">
                Generate random phone numbers with our free online tool. Create realistic phone numbers for testing, validation, and privacy protection.
            </ToolPageIntro>

                <Card className="mb-8">
                    <CardHeader>
                        <div className="flex justify-between items-center">
                            <CardTitle>Generated Phone Number</CardTitle>
                            <div className="flex space-x-2">
                                <Button variant="outline" size="sm" onClick={copyToClipboard} disabled={!phoneNumber || copied}>
                                    <Copy className="h-4 w-4 mr-2" />
                                    {copied ? 'Copied!' : 'Copy'}
                                </Button>
                                <Button variant="outline" size="sm" onClick={generatePhoneNumber}>
                                    <RefreshCw className="h-4 w-4 mr-2" />
                                    Generate New
                                </Button>
                            </div>
                        </div>
                        <CardDescription>Select a country to generate a phone number in that format</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium leading-none flex items-center">
                                        <Globe className="h-4 w-4 mr-2" />
                                        Country
                                    </label>
                                    <select
                                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                        value={country.name}
                                        onChange={(e) => {
                                            const selected = COUNTRY_FORMATS.find(c => c.name === e.target.value) || COUNTRY_FORMATS[0];
                                            setCountry(selected);
                                            generatePhoneNumber();
                                        }}
                                    >
                                        {COUNTRY_FORMATS.map((c) => (
                                            <option key={c.name} value={c.name}>
                                                {c.flag} {c.name} ({c.code})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium leading-none flex items-center">
                                        <Hash className="h-4 w-4 mr-2" />
                                        Number Format
                                    </label>
                                    <div className="flex h-10 w-full rounded-md border border-input bg-muted/50 px-3 py-2 text-sm items-center">
                                        {country.format}
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium leading-none flex items-center">
                                    <Smartphone className="h-4 w-4 mr-2" />
                                    Phone Number
                                </label>
                                <div className="bg-muted/50 p-6 rounded-lg font-mono text-xl text-center">
                                    {country.code} {phoneNumber}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <div className="grid gap-6 md:grid-cols-2">
                    <Card>
                        <CardHeader>
                            <CardTitle>Formats</CardTitle>
                            <CardDescription>Different phone number formats</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div>
                                <p className="text-sm text-muted-foreground mb-1">International Format</p>
                                <p className="bg-muted/50 p-3 rounded">
                                    {country.code} {phoneNumber}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground mb-1">Local Format</p>
                                <p className="bg-muted/50 p-3 rounded">
                                    {phoneNumber}
                                </p>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Country Information</CardTitle>
                            <CardDescription>Details about the selected country</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center space-x-2">
                                <Flag className="h-5 w-5 text-muted-foreground" />
                                <div>
                                    <p className="text-sm text-muted-foreground">Country</p>
                                    <p>{country.name} {country.flag}</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Hash className="h-5 w-5 text-muted-foreground" />
                                <div>
                                    <p className="text-sm text-muted-foreground">Country Code</p>
                                    <p>{country.code}</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Smartphone className="h-5 w-5 text-muted-foreground" />
                                <div>
                                    <p className="text-sm text-muted-foreground">Phone Format</p>
                                    <p>{country.format}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={randomPhoneGeneratorContent.title}
                description={randomPhoneGeneratorContent.description}
                content={randomPhoneGeneratorContent.content}
                faqs={randomPhoneGeneratorContent.faqs}
                relatedTools={randomPhoneGeneratorContent.relatedTools}
            />
        </div>
    );
}
